// src/phase4/ui/WaveInfoPanel.ts
// Wave information panel - Pure Phaser UI component
// Displays current wave number and Start Wave button

import Phaser from 'phaser';

export class WaveInfoPanel extends Phaser.GameObjects.Container {
  private waveText?: Phaser.GameObjects.Text;
  private startBtnContainer?: Phaser.GameObjects.Container;
  private enemyCountText?: Phaser.GameObjects.Text;
  private onStartWave?: () => void;
  private isWaveActive: boolean = false;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    scene.add.existing(this);
    this.createPanel();
    this.setDepth(100);
  }

  private createPanel(): void {
    // Panel background
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x201e1a, 0.9);
    bg.fillRoundedRect(-180, -25, 360, 50, 8);
    bg.lineStyle(1, 0x3a362e);
    bg.strokeRoundedRect(-180, -25, 360, 50, 8);
    this.add(bg);

    // Wave text
    this.waveText = this.scene.add.text(-150, 0, 'Wave 1/7', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'monospace',
      fontStyle: 'bold',
    }).setOrigin(0, 0.5);
    this.add(this.waveText);

    // Enemy count
    this.enemyCountText = this.scene.add.text(-150, 22, 'Enemies: 0', {
      fontSize: '12px',
      color: '#9b9488',
      fontFamily: 'monospace',
    }).setOrigin(0, 0.5);
    this.add(this.enemyCountText);

    // Start Wave button
    this.startBtnContainer = this.createButton('Start Wave', 100, 0, 140, 40, 0x4a7c59, () => {
      if (!this.isWaveActive) {
        this.scene.sound.play('click');
        this.onStartWave?.();
      }
    });
    this.add(this.startBtnContainer);
  }

  private createButton(
    label: string,
    x: number,
    y: number,
    width: number,
    height: number,
    bgColor: number,
    onClick: () => void
  ): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);
    container.setSize(width, height);

    const btnBg = this.scene.add.graphics();
    btnBg.fillStyle(bgColor, 1);
    btnBg.fillRoundedRect(-width / 2, -height / 2, width, height, 6);
    container.add(btnBg);

    const btnText = this.scene.add.text(0, 0, label, {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'monospace',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(btnText);

    const hitArea = this.scene.add.rectangle(0, 0, width, height, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true })
      .on('pointerdown', () => {
        onClick();
      });
    container.add(hitArea);

    return container;
  }

  update(currentWave: number, totalWaves: number, enemyCount: number = 0): void {
    this.waveText?.setText(`Wave ${currentWave}/${totalWaves}`);
    this.enemyCountText?.setText(`Enemies: ${enemyCount}`);
  }

  setWaveActive(active: boolean): void {
    this.isWaveActive = active;
    if (this.startBtnContainer) {
      const children = this.startBtnContainer.list;
      const bg = children.find((c) => c.type === 'Graphics') as Phaser.GameObjects.Graphics | undefined;
      if (bg) {
        bg.clear();
        if (active) {
          bg.fillStyle(0x34312b, 1);
          bg.fillRoundedRect(-70, -20, 140, 40, 6);
        } else {
          bg.fillStyle(0x4a7c59, 1);
          bg.fillRoundedRect(-70, -20, 140, 40, 6);
        }
      }
    }
  }

  setOnStartWave(cb: () => void): void {
    this.onStartWave = cb;
  }

  setPosition(x: number, y: number): this {
    super.setPosition(x, y);
    return this;
  }

  destroy(): void {
    super.destroy();
  }
}

export default WaveInfoPanel;
